// M2R.5 — HomePage với shared-terminal kiosk UX.
//
// Flow (user=kiosk):
//   1. Header: logo + "Máy: {display_name} · Shop {X}" + nút "Đăng xuất máy"
//   2. Nếu CHƯA có L2 (không có NV active) → hiển thị EmployeePicker lớn giữa trang
//      NV bấm tên → mở L2SetupModal (lần đầu) hoặc L2OtpModal (đã enable)
//   3. Nếu ĐÃ có L2 → banner "NV: {full_name}" + "Thoát NV" + 4 action tile + widget
//
// Flow (user=admin): giữ nguyên — admin bypass L2, hiển thị action tiles + mgmt cards.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
import EmployeePicker from '../components/EmployeePicker.jsx';
import L2OtpModal from '../components/L2OtpModal.jsx';
import L2SetupModal from '../components/L2SetupModal.jsx';
import FlashcardProgressWidget from '../components/FlashcardProgressWidget.jsx';

export default function HomePage() {
  const { user, refreshToken, logout, l2Employee, l2SessionId, getValidL2SessionId, clearL2Session } = useAuthStore();
  const [health, setHealth] = useState(null);
  const [picked, setPicked] = useState(null); // { employee, kind: 'setup'|'verify' }
  // Progress flashcard — hoist để tile "Bắt đầu học" dùng được due_count
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressErr, setProgressErr] = useState(null);
  // 2026-04-24: Feature flags — ẩn "Xem thưởng" nếu admin tắt
  const [features, setFeatures] = useState({ rewards_enabled: true });
  const nav = useNavigate();

  useEffect(() => { api.health().then(setHealth).catch(() => {}); }, []);
  useEffect(() => {
    api.getFeatureFlags().then(setFeatures).catch(() => {});
  }, []);

  // Kiểm tra L2 session còn hạn mỗi khi focus lại cửa sổ (máy chung, phiên tự hết)
  useEffect(() => {
    const onFocus = () => { getValidL2SessionId(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [getValidL2SessionId]);

  const isAdmin = user?.role === 'admin';
  const isKiosk = user?.role === 'kiosk';
  const hasValidL2 = !!getValidL2SessionId() && !!l2Employee;

  // Fetch flashcard progress CHỈ khi NV đã L2 (admin không học flashcard).
  // 2026-04-24: bỏ fetch cho admin — không có employee_id nên BE trả fail,
  // gây UI hiện "Không lấy được tiến trình flashcard" lỗi không cần thiết.
  // Admin cần xem tiến trình NV → vào /admin/progress.
  // + refetch khi tab visible lại (NV học xong card → quay về HomePage)
  useEffect(() => {
    if (!hasValidL2) { setProgress(null); return; }
    let cancelled = false;
    async function loadProgress() {
      setProgressLoading(true);
      setProgressErr(null);
      try {
        const r = await api.flashcardProgress();
        if (!cancelled) setProgress(r);
      } catch (e) {
        if (!cancelled) setProgressErr(e);
      } finally {
        if (!cancelled) setProgressLoading(false);
      }
    }
    loadProgress();

    // Refetch khi tab focus lại (NV vừa học xong → quay về HomePage)
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadProgress();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [hasValidL2, l2Employee?.id, l2SessionId]);

  const dueCount = progress?.due_now ?? 0;
  const hasDue = dueCount > 0;

  // M46 (2026-04-25): fetch rules modules để đếm câu chưa học
  const [rulesModules, setRulesModules] = useState(null);
  useEffect(() => {
    if (!hasValidL2) { setRulesModules(null); return; }
    let cancelled = false;
    api.rulesModulesList()
      .then(r => { if (!cancelled) setRulesModules(r?.modules || []); })
      .catch(() => { if (!cancelled) setRulesModules([]); });
    return () => { cancelled = true; };
  }, [hasValidL2, l2Employee?.id]);

  // M89b (2026-04-26): fetch round state M80 (số chương đã học/tổng + vòng)
  const [rulesRound, setRulesRound] = useState(null);
  useEffect(() => {
    if (!hasValidL2) { setRulesRound(null); return; }
    let cancelled = false;
    function load() {
      api.rulesM80RoundCurrent()
        .then(r => { if (!cancelled) setRulesRound(r); })
        .catch(() => { if (!cancelled) setRulesRound(null); });
    }
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [hasValidL2, l2Employee?.id, l2SessionId]);

  // M84 (2026-04-26): fetch nhiệm vụ hôm nay (flashcard target + rules target)
  const [dailyTasks, setDailyTasks] = useState(null);
  useEffect(() => {
    if (!hasValidL2) { setDailyTasks(null); return; }
    let cancelled = false;
    function load() {
      api.meDailyTasks()
        .then(r => { if (!cancelled) setDailyTasks(r); })
        .catch(() => { if (!cancelled) setDailyTasks(null); });
    }
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [hasValidL2, l2Employee?.id, l2SessionId]);

  // M46: Số thẻ CHƯA học (học kiến thức sản phẩm) — 3 bộ thưởng,
  // "chưa học" = total - mastered - in_progress (chưa tiếp xúc)
  const learnUnseenCount = (() => {
    if (!progress?.by_set) return null;
    const rewardSets = ['bo_1000k', 'bo_1250k', 'bo_nang_cao'];
    let unseen = 0;
    for (const s of progress.by_set) {
      if (!rewardSets.includes(s.set)) continue;
      const unstarted = Math.max(0, (s.total || 0) - (s.mastered || 0) - (s.in_progress || 0));
      unseen += unstarted;
    }
    return unseen;
  })();

  // M89b (2026-04-26): subtitle cho ActionTile "Học nội quy" — M80 round state
  // Hiện "X/N chương · Vòng K" thay "Đã học hết" (M35 cũ không còn dùng).
  const rulesSubtitle = (() => {
    if (!rulesRound?.modules) return null;
    const total = rulesRound.modules.length;
    const done = rulesRound.modules.filter(m => m.completed || m.is_reviewed).length;
    const round = rulesRound.round?.round_no || 1;
    if (total === 0) return null;
    return `${done}/${total} chương · Vòng ${round}`;
  })();

  // M46 (legacy, giữ cho M35 backward-compat — không hiển thị nữa sau M89b)
  const rulesUnansweredCount = (() => {
    if (!Array.isArray(rulesModules)) return null;
    return rulesModules.reduce(
      (s, m) => s + Math.max(0, (m.question_count || 0) - (m.completed_count || 0)),
      0,
    );
  })();

  async function doLogoutMachine() {
    const ok = await showConfirm({
      title: 'Đăng xuất máy',
      message: 'Đăng xuất máy? Mọi phiên đang mở sẽ bị ngắt.',
      confirmText: 'Đăng xuất máy',
      dangerous: true,
    });
    if (!ok) return;
    try { await api.logout(refreshToken); } catch {}
    logout();
    toast.success('Đã đăng xuất máy');
    nav('/login');
  }

  async function doLogoutEmployee() {
    if (!l2SessionId) return;
    try { await api.l2Logout(l2SessionId); } catch {}
    clearL2Session();
    toast.success('Đã thoát — máy vẫn đăng nhập');
  }

  // Gate cho 4 action tile (chỉ enable khi có L2 hoặc admin)
  function guarded(action, label) {
    return () => {
      if (isAdmin) { action(); return; }
      if (!hasValidL2) { toast.error('Vui lòng chọn nhân viên và xác minh L2 trước'); return; }
      action();
    };
  }

  const doLearn     = guarded(() => nav('/flashcards?mode=learn'),                   'Học kiến thức sản phẩm');
  const doQuiz      = guarded(() => nav('/exam'),                                   'Kiểm tra lấy thưởng');
  const doAsk       = guarded(() => nav('/ask'),                                    'Hỏi đáp');
  // 2026-04-24: Đã có RewardsPage thật — nav('/rewards')
  const doRewards   = guarded(() => nav('/rewards'),                                'Xem thưởng');
  // M126 (2026-04-27): doBookmarks đã chuyển sang AskPage. Giữ comment để tracking.
  // M9.9: shortcut đến nội quy (tree + scroll gate + ack)
  const doNoiQuy    = guarded(() => nav('/noi-quy'),                                'Nội quy');

  return (
    <div className="min-h-full p-6">
      <div className="max-w-4xl mx-auto">
        {/* ========= HEADER MÁY ========= */}
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-brand-700">Hello Con Đào Tạo</h1>
            <p className="text-sm text-slate-500">
              Máy: <span className="font-medium">{user?.display_name}</span>
              <span className="mx-2">•</span>
              Shop <span className="font-mono">{user?.shop_id ?? '—'}</span>
              <span className="mx-2">•</span>
              Vai trò <span className="font-mono">{user?.role}</span>
            </p>
          </div>
          <div className="flex flex-col items-end">
            <button
              onClick={doLogoutMachine}
              className="px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-medium border border-rose-200"
              data-testid="logout-machine"
            >
              Đăng xuất máy
            </button>
            <span className="mt-1 text-[11px] text-rose-500/80 italic select-none">
              (nhân viên không nên bấm)
            </span>
          </div>
        </header>

        {/* ========= BANNER NV ĐANG HOẠT ĐỘNG ========= */}
        {isKiosk && hasValidL2 && (
          <div className="mb-5 bg-gradient-to-r from-brand-50 to-emerald-50 border border-brand-200 rounded-2xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold">
                {initials(l2Employee?.full_name)}
              </div>
              <div>
                <div className="text-xs text-brand-700/70">Đang đăng nhập</div>
                <div className="font-semibold text-brand-800">{l2Employee?.full_name}</div>
              </div>
            </div>
            <button
              onClick={doLogoutEmployee}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white text-base font-bold border-2 border-orange-400 shadow-lg shadow-orange-200 hover:shadow-orange-300 hover:scale-105 transition-all"
              data-testid="logout-employee"
              title="Bấm khi bạn làm xong ca — để người tiếp theo có thể đăng nhập"
            >
              🚪 Thoát
            </button>
          </div>
        )}

        {/* ========= EMPLOYEE PICKER (kiosk chưa pick NV) ========= */}
        {isKiosk && !hasValidL2 && (
          <section className="mb-6">
            <EmployeePicker onPick={(emp, kind) => setPicked({ employee: emp, kind })} />
          </section>
        )}

        {/* M84 (2026-04-26): Nhiệm vụ hôm nay — gợi ý NV biết tối thiểu cần làm gì */}
        {hasValidL2 && dailyTasks && (
          <DailyTasksCard tasks={dailyTasks} />
        )}

        {/* ========= 4 ACTION TILE ========= */}
        {/* 2026-04-24: HomePage giờ CHỈ cho kiosk (admin redirect /admin).
             Guard hasValidL2 đảm bảo các nút chỉ hiện khi NV đã login L2. */}
        {hasValidL2 && (
          <section className="mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ActionTile title="Hỏi đáp"          icon="🔍" color="sky"     onClick={doAsk}     dataTestid="action-ask" />
              <ActionTile
                title="Học nội quy"
                icon="📋" color="rose"
                onClick={doNoiQuy}
                dataTestid="action-noiquy"
                subtitle={rulesSubtitle}
              />
              <ActionTile
                title="Học kiến thức sản phẩm"
                icon="📚"
                color="brand"
                onClick={doLearn}
                dataTestid="action-learn"
                dim={hasDue}
                tooltip={hasDue ? `Bạn có ${dueCount} thẻ đến hạn ôn — ưu tiên ôn trước nhé` : null}
                unseenCount={learnUnseenCount}
              />
              <ActionTile title="Kiểm tra lấy thưởng" icon="📝" color="indigo"  onClick={doQuiz}    dataTestid="action-quiz"/>
            </div>
            {hasDue && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-base leading-none">💡</span>
                <span>
                  <strong className="font-semibold">Gợi ý:</strong> bạn có <strong>{dueCount}</strong> thẻ đến hạn ôn.
                  Ôn xong rồi học bài mới sẽ nhớ lâu hơn — nhấn <em>Ôn ngay</em> ở ô bên dưới.
                </span>
              </div>
            )}
            {/* M126 (2026-04-27): Bookmark shortcut đã chuyển sang trang /ask
                (Hỏi đáp) — NV xem câu đã lưu ngay trong feature họ đang dùng,
                tránh nhồi nhét HomePage. */}
            {/* Xem thưởng shortcut — chỉ hiện khi admin bật chế độ thưởng */}
            {features.rewards_enabled && (
              <div className="mt-3 flex items-center justify-between bg-emerald-50/60 border border-emerald-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-emerald-800">
                  <span className="text-base leading-none">🎁</span>
                  <span>Xem tổng tiền thưởng bạn đã đạt được từ các bộ kiểm tra đã đậu.</span>
                </div>
                <button
                  type="button"
                  onClick={doRewards}
                  className="shrink-0 px-3 py-1 rounded-md bg-white hover:bg-emerald-100 text-emerald-800 text-xs font-semibold border border-emerald-300 transition"
                  data-testid="action-rewards"
                >
                  Xem thưởng →
                </button>
              </div>
            )}
          </section>
        )}

        {/* ========= PROGRESS WIDGET — CHỈ NV đã L2 thấy (admin không có progress) ========= */}
        {hasValidL2 && (
          <section className="mb-4">
            <FlashcardProgressWidget
              data={progress}
              loading={progressLoading}
              error={progressErr}
            />
          </section>
        )}

        {/* 2026-04-24: Mgmt cards đã move sang AdminHomePage (/admin).
             HomePage giờ chỉ phục vụ NV kiosk. */}
      </div>

      {/* ========= L2 MODALS ========= */}
      <L2SetupModal
        open={picked?.kind === 'setup'}
        employee={picked?.employee || null}
        onClose={() => setPicked(null)}
        onSuccess={() => setPicked(null)}
      />
      <L2OtpModal
        open={picked?.kind === 'verify'}
        employee={picked?.employee || null}
        onClose={() => setPicked(null)}
        onSuccess={() => setPicked(null)}
        actionLabel="Đăng nhập"
      />
    </div>
  );
}

// M84 (2026-04-26): Card nhiệm vụ hôm nay — 2 progress bars (flashcard + rules).
// Mục đích: NV biết tối thiểu cần làm gì hôm nay để không quên.
// M125 (2026-04-27): Flashcard tách 2 sub-task (mới + ôn) — mỗi cái có target
// riêng, cap theo pool còn lại. Trước đây gộp counter "22/10" gây hiểu nhầm.
function DailyTasksCard({ tasks }) {
  const rules = tasks?.rules || { target: 0, done: 0 };

  // M125: BE trả flashcard.new + flashcard.review (sub-task tách bạch)
  const fcNew    = tasks?.flashcard?.new    || { target: 0, done: 0 };
  const fcReview = tasks?.flashcard?.review || { target: 0, done: 0 };
  const fcNewPct    = fcNew.target > 0    ? Math.min(100, Math.round((fcNew.done    / fcNew.target)    * 100)) : 0;
  const fcReviewPct = fcReview.target > 0 ? Math.min(100, Math.round((fcReview.done / fcReview.target) * 100)) : 0;
  const fcNewMet    = fcNew.target === 0    || fcNew.done    >= fcNew.target;
  const fcReviewMet = fcReview.target === 0 || fcReview.done >= fcReview.target;
  const fcDone = fcNewMet && fcReviewMet && (fcNew.target > 0 || fcReview.target > 0);

  const rulesPct = rules.target > 0 ? Math.min(100, Math.round((rules.done / rules.target) * 100)) : 0;
  const rulesDone = rules.done >= rules.target && rules.target > 0;
  const allDone = fcDone && rulesDone;

  return (
    <section className="mb-4 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-2 border-amber-200 rounded-2xl p-4 md:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">🎯</span>
        <h2 className="text-lg font-bold text-slate-800">Nhiệm vụ hôm nay</h2>
        {allDone && (
          <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full border border-emerald-300">
            ✅ Đã hoàn thành
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* M125 — Flashcard task: tách 2 sub-progress (mới + ôn) trong cùng card */}
        {(fcNew.target > 0 || fcReview.target > 0) && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">📚</span>
                <span className="text-sm font-semibold text-slate-700">Học &amp; ôn thẻ</span>
              </div>
              {fcDone && (
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  ✓ Đủ
                </span>
              )}
            </div>

            {/* Sub-task: thẻ MỚI */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-600">🌱 Thẻ mới</span>
                <span className={`font-bold ${fcNewMet ? 'text-emerald-600' : 'text-slate-700'}`}>
                  {fcNew.done}<span className="text-slate-400">/{fcNew.target}</span>
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    fcNewMet ? 'bg-emerald-500' : 'bg-gradient-to-r from-sky-400 to-indigo-500'
                  }`}
                  style={{ width: `${fcNewPct}%` }}
                />
              </div>
              {!fcNewMet && fcNew.target > 0 && (
                <div className="text-[10px] text-amber-700 mt-0.5">
                  cần thêm {fcNew.target - fcNew.done} thẻ mới
                </div>
              )}
            </div>

            {/* Sub-task: thẻ ÔN */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-600">🔁 Thẻ ôn</span>
                <span className={`font-bold ${fcReviewMet ? 'text-emerald-600' : 'text-slate-700'}`}>
                  {fcReview.done}<span className="text-slate-400">/{fcReview.target}</span>
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    fcReviewMet ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'
                  }`}
                  style={{ width: `${fcReviewPct}%` }}
                />
              </div>
              {!fcReviewMet && fcReview.target > 0 && (
                <div className="text-[10px] text-amber-700 mt-0.5">
                  cần thêm {fcReview.target - fcReview.done} thẻ ôn
                </div>
              )}
            </div>

            {/* Note nếu target bị cap do pool ít */}
            {(fcNew.target < (fcNew.setting || 10) || fcReview.target < (fcReview.setting || 10)) && (
              <div className="text-[10px] text-slate-400 italic pt-0.5">
                💡 Chỉ tiêu hôm nay đã được điều chỉnh theo số thẻ còn lại trong pool.
              </div>
            )}
          </div>
        )}

        {/* Rules task */}
        {rules.target > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">📋</span>
                <span className="text-sm font-semibold text-slate-700">Học chương nội quy</span>
              </div>
              <span className={`text-sm font-bold ${rulesDone ? 'text-emerald-600' : 'text-slate-700'}`}>
                {rules.done}<span className="text-slate-400">/{rules.target}</span>
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-1.5">
              <div
                className={`h-full transition-all duration-500 ${
                  rulesDone ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                            : 'bg-gradient-to-r from-rose-400 to-pink-500'
                }`}
                style={{ width: `${rulesPct}%` }}
              />
            </div>
            <div className="text-[11px] text-slate-500">
              {rulesDone
                ? `Đã hoàn thành ${rules.done} chương — tuyệt vời!`
                : <>Cần hoàn thành thêm <span className="font-semibold text-amber-700">{rules.target - rules.done}</span> chương trong hôm nay</>}
            </div>
          </div>
        )}
      </div>

      {!allDone && (
        <div className="mt-3 text-xs text-amber-800 italic">
          💡 Mỗi ngày học đủ tối thiểu để giữ chuỗi học tập.
        </div>
      )}
    </section>
  );
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ActionTile({ title, icon, color, onClick, dataTestid, dim = false, tooltip = null, unseenCount = null, subtitle = null }) {
  const palette = {
    brand:   'bg-brand-50   hover:bg-brand-100   text-brand-700   border-brand-200',
    indigo:  'bg-indigo-50  hover:bg-indigo-100  text-indigo-700  border-indigo-200',
    sky:     'bg-sky-50     hover:bg-sky-100     text-sky-700     border-sky-200',
    emerald: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200',
    rose:    'bg-rose-50    hover:bg-rose-100    text-rose-700    border-rose-200',
  }[color] || 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200';
  const dimCls = dim ? 'opacity-60 hover:opacity-90' : '';

  // M46 (2026-04-25): unseenCount
  //   null → chưa load / không áp dụng → không hiện subtitle
  //   0    → đã học hết → badge emerald "✓ Đã học hết"
  //   > 0  → số lớn gradient animated + pulsing dot notification badge góc phải trên
  const hasUnseen = typeof unseenCount === 'number' && unseenCount > 0;
  const isDone = unseenCount === 0;

  return (
    <button
      onClick={onClick}
      data-testid={dataTestid}
      title={tooltip || undefined}
      className={`relative rounded-2xl border ${palette} ${dimCls} p-4 text-left transition shadow-sm hover:shadow overflow-hidden`}
    >
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 text-sm font-semibold">{title}</div>

      {/* M89b (2026-04-26): subtitle text custom (M80 round state) */}
      {subtitle && (
        <div className="mt-1.5 text-[12px] font-semibold text-rose-700 leading-tight">
          {subtitle}
        </div>
      )}

      {/* Subtitle: số lớn animated cho unseen, emerald done */}
      {hasUnseen && (
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className="text-xl font-extrabold tabular-nums bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 bg-clip-text text-transparent">
            {unseenCount}
          </span>
          <span className="text-[11px] font-medium text-slate-600">thẻ chưa học</span>
        </div>
      )}
      {isDone && (
        <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold border border-emerald-200">
          <span>✓</span>
          <span>Đã học hết</span>
        </div>
      )}

      {dim && tooltip && (
        <span className="absolute -top-1 -left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-semibold shadow-sm animate-pulse">
          ⏰
        </span>
      )}
    </button>
  );
}

function Card({ title, note, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-5 ${className}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-400">{note}</span>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
